import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1758270804188-8ca0b6d254bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHx0ZXh0aWxlJTIwd2VhdmluZyUyMGxvb20lMjBmYWN0b3J5JTIwd2hpdGUlMjBmYWJyaWN8ZW58MHx8fHwxNzc2MDkxODM2fDA&ixlib=rb-4.1.0&q=85";
const LOGO_IMG = "https://customer-assets.emergentagent.com/job_yarn-weight-pricer/artifacts/6sx1m0su_WhatsApp%20Image%202026-04-13%20at%207.20.45%20PM.jpeg";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Hero image - hidden on mobile */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end p-12 login-hero"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      >
        <div className="relative z-10 text-white max-w-md">
          <h1 className="font-heading text-4xl font-bold mb-3 leading-tight">
            Precision Costing for Every Thread
          </h1>
          <p className="text-white/80 text-lg leading-relaxed">
            Calculate dhoti production costs with accuracy. From warp to weft, every yarn accounted for.
          </p>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm space-y-8">
          <div className="space-y-3">
            <img src={LOGO_IMG} alt="Logo" className="h-20 mx-auto mb-6" />
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
              Sign in
            </h2>
            <p className="text-sm text-muted-foreground">
              Enter your credentials to access the costing calculator
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="admin@ministerwhite.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#C2410C] hover:bg-[#9A3412] text-white font-medium"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground pt-4">
            Dhoti Costing Calculator &middot; Textile Production Management
          </p>
        </div>
      </div>
    </div>
  );
}
